import {
  Client,
  createOptimizelyInstance,
  userIdPrefix,
  zeroUUID,
} from '@sbt-web/optimizely-sdk';
import logger from '../server/logger';

const SHOULD_MIRROR_REQUEST =
  'subito.web.internal-linking.mirror-request-to-new-api';
const METRIC_COMPARISON_SUCCESS = 'mirrored-plexus-request-responses-identical';
const METRIC_COMPARISON_FAILURE = 'mirrored-plexus-request-responses-mismatch';

// Global instance shared between **all connections**.
// There's no need to close it.
let globalInstance: Client | null | undefined;

export async function getOptimizelyClient(): Promise<Client | null> {
  globalInstance ??= createOptimizelyInstance(
    process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT,
    userIdPrefix,
    logger
  );

  try {
    await globalInstance?.onReady({
      timeout: Number.parseInt(process.env.HOUSTON_SERVER_TIMEOUT_MS),
    });
  } catch {
    return null;
  }
  return globalInstance;
}

/**
 * Checks if the request should be mirrored to the new Plexus service
 * based on the feature flag in Houston.
 * @returns {Promise<boolean>} True if the request should be mirrored, false otherwise.
 */
export async function shouldMirrorRequest(
  optimizelyClient: Client | null,
  envId: string
): Promise<boolean> {
  return (
    optimizelyClient?.isFeatureEnabled(SHOULD_MIRROR_REQUEST, envId) ?? false
  );
}

/**
 * Compares responses from original and mirrored Plexus services
 * @param originalResponse Response from the original Plexus service
 * @param mirrorResponse Response from the mirrored Plexus service
 * @param requestURL The URL that was requested
 * @param optimizelyClient The Optimizely client instance
 * @param envId The environment ID for tracking
 */
export async function comparePlexusResponses(
  originalResponse: Response,
  mirrorResponse: Response,
  requestURL: URL,
  optimizelyClient: Client | null,
  envId: string
): Promise<void> {
  try {
    // Skip comparison if either response failed
    if (!originalResponse.ok || !mirrorResponse.ok) {
      console.warn(
        'Skipping Plexus mirror comparison - one or both responses failed',
        {
          originalStatus: originalResponse.status,
          mirrorStatus: mirrorResponse.ok ? mirrorResponse.status : 'Failed',
          url: requestURL.toString(),
        }
      );
      return;
    }

    // Parse JSON responses
    const originalData = await originalResponse.json();
    const mirrorData = await mirrorResponse.json();
    // Compare result count
    const originalResultCount = originalData.results?.length ?? 0;
    const mirrorResultCount = mirrorData.results?.length ?? 0;
    if (originalResultCount > mirrorResultCount) {
      await sendMetrics(false, optimizelyClient, envId);
      console.warn('Plexus mirror results less than original', {
        originalCount: originalResultCount,
        mirrorCount: mirrorResultCount,
        url: requestURL.toString(),
      });
      logger.logWarn(
        `[PLEXUS] Plexus mirror results less than original, ${originalResultCount}, ${mirrorResultCount}, ${requestURL.toString()}`
      );
      return;
    }
    // If we reach this point, the responses are considered equivalent
    await sendMetrics(true, optimizelyClient, envId);
  } catch (error) {
    console.error('Error comparing Plexus response with mirror:', error);
    logger.logError(
      `[PLEXUS] Error comparing Plexus response with mirror ${error}`
    );
  }
}

async function sendMetrics(
  success: boolean,
  optimizelyClient: Client | null,
  envId: string = zeroUUID
): Promise<void> {
  optimizelyClient?.track(
    success ? METRIC_COMPARISON_SUCCESS : METRIC_COMPARISON_FAILURE,
    envId
  );
}
